import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";
import isEmpty from "lodash/isEmpty";
import {
  renderMainDataPoint,
  renderSingleWithLabel,
  getAlleleFrequency,
} from "../../../../common/helpers/helpers";
import {
  getIcgcSomatic,
  getIcgcSomaticProperties,
  getifOldIcgc,
} from "../../../../state/selectors/IcgcSomatic.selectors";
import { Table } from "../../../../components/atoms";
import ChartsGroup from "../../../../components/atoms/charts/ChartsGroup";
import Chart from "../../../../components/atoms/charts/Chart";
import withErrorGuard from "../../../../HOC/withErrorGuard";
import ComponentPanel from "../../../../components/atoms/ComponentPanel/ComponentPanel";
import HelperText from "../../../../components/atoms/HelperText";

const PROJECT_TUMOR = {
  "ALL-US": "Acute Lymphoblastic Leukemia",
  "AML-US": "Acute Myeloid Leukemia",
  "BLCA-CN": "Bladder Cancer",
  "BLCA-US": "Bladder Urothelial Cancer",
  "BOCA-FR": "Soft Tissue cancer",
  "BOCA-UK": "Bone Cancer",
  "BRCA-EU": "Breast ER+ and HER2- Cancer",
  "BRCA-FR": "Breast Cancer",
  "BRCA-KR": "Breast Cancer",
  "BRCA-UK": "Breast Triple Negative/Lobular Cancer",
  "BRCA-US": "Breast Cancer",
  "BTCA-JP": "Biliary Tract Cancer",
  "BTCA-SG": "Biliary Tract Cancer",
  "CCSK-US": "Clear Cell Sarcomas of the Kidney",
  "CESC-US": "Cervical Squamous Cell Carcinoma",
  "CLLE-ES": "Chronic Lymphocytic Leukemia",
  "CMDI-UK": "Chronic Myeloid Disorders",
  "COAD-US": "Colon Adenocarcinoma",
  "COCA-CN": "Colorectal Cancer",
  "DLBC-US": "Lymphoid Neoplasm Diffuse Large B-cell Lymphoma",
  "EOPC-DE": "Early Onset Prostate Cancer",
  "ESAD-UK": "Esophageal Adenocarcinoma",
  "ESCA-CN": "Esophageal Cancer",
  "GACA-CN": "Gastric Cancer",
  "GBM-US": "Brain Glioblastoma Multiforme",
  "HNSC-US": "Head and Neck Squamous Cell Carcinoma",
  "KICH-US": "Kidney Chromophobe",
  "KIRC-US": "Kidney Renal Clear Cell Carcinoma",
  "KIRP-US": "Kidney Renal Papillary Cell Carcinoma",
  "LAML-CN": "Leukemia",
  "LAML-KR": "Acute Myeloid Leukemia",
  "LAML-US": "Acute Myeloid Leukemia",
  "LGG-US": "Brain Lower Grade Glioma",
  "LIAD-FR": "Benign Liver Tumor",
  "LICA-CN": "Liver Cancer",
  "LICA-FR": "Liver Cancer",
  "LIHC-US": "Liver Hepatocellular carcinoma",
  "LIHM-FR": "Liver Cancer",
  "LINC-JP": "Liver Cancer",
  "LIRI-JP": "Liver Cancer",
  "LUAD-US": "Lung Adenocarcinoma",
  "LUSC-CN": "Lung Cancer",
  "LUSC-KR": "Lung Cancer",
  "LUSC-US": "Lung Squamous Cell Carcinoma",
  "MALY-DE": "Malignant Lymphoma",
  "MELA-AU": "Skin Cancer",
  "NBL-US": "Neuroblastoma",
  "ORCA-IN": "Oral Cancer",
  "OV-AU": "Ovarian Cancer",
  "OV-US": "Ovarian Serous Cystadenocarcinoma",
  "PAAD-US": "Pancreatic Cancer",
  "PACA-AU": "Pancreatic Cancer",
  "PACA-CA": "Pancreatic Cancer",
  "PAEN-AU": "Pancreatic Cancer Endocrine neoplasms",
  "PAEN-IT": "Pancreatic Endocrine Neoplasms",
  "PBCA-DE": "Pediatric Brain Cancer",
  "PRAD-CA": "Prostate Adenocarcinoma",
  "PRAD-UK": "Prostate Adenocarcinoma",
  "PRAD-US": "Prostate Adenocarcinoma",
  "READ-US": "Rectum Adenocarcinoma",
  "RECA-CN": "Renal Cancer",
  "RECA-EU": "Renal Cell Cancer",
  "SARC-US": "Sarcoma",
  "SKCA-BR": "Skin Adenocarcinoma",
  "SKCM-US": "Skin Cutaneous melanoma",
  "STAD-US": "Gastric Adenocarcinoma",
  "THCA-SA": "Thyroid Cancer",
  "THCA-US": "Head and Neck Thyroid Carcinoma",
  "UCEC-US": "Uterine Corpus Endometrial Carcinoma",
  "WT-US": "Wilms Tumor",
};

class IcgcSomatic extends React.Component {
  constructor() {
    super();
    this.state = {};
  }

  render() {
    const { icgcSomatic, icgcSomaticProperties, isOldIcgc } = this.props;

    if (!icgcSomatic || isEmpty(icgcSomatic)) return null;

    const description = icgcSomaticProperties;

    // icgcSomatic is an array of objects both in old and in new icgc database versions.
    const values = icgcSomatic;

    const { version } = values[0];
    const titleDetails = isOldIcgc ? values[0].main_data : "";

    const oldIcgc =
      isOldIcgc &&
      // added the values in an array because we access the zero index from the selector
      // in order to not access it every time in here
      // the map is not needed here
      values.map((item) => {
        let totalDonors = 0;
        let occurrences;
        if (item.occurrence) {
          for (let i = 0; i < item.occurrence.length; i++) {
            totalDonors += item.occurrence[i].donors;
          }

          // Calculate frequencies for every occurence
          item.occurrence = item.occurrence.map((elem) => {
            if (elem.affected !== null && elem.donors) {
              elem.frequency = elem.affected / elem.donors;
            } else {
              elem.frequency = 0;
            }

            return elem;
          });
          // Sort occurrence by frequency(desc)
          occurrences = item.occurrence.sort((a, b) => {
            return b.frequency - a.frequency;
          });
        }

        return (
          <div className="varsome-inner-container">
            <div className="varsome-horiz-flex">
              {item.id && (
                <div className="varsome-horiz-list-item">
                  <div className="varsome-horiz-list-label">
                    {description.items.properties.id.label}
                  </div>
                  <div className="varsome-horiz-list-value">
                    <div>{item.id}</div>
                  </div>
                </div>
              )}
              <div className="varsome-horiz-list-item">
                <div className="varsome-horiz-list-label">Total donors</div>
                <div className="varsome-horiz-list-value">{totalDonors}</div>
              </div>
              {renderSingleWithLabel(
                item,
                "affected_donors",
                description.items.properties.affected_donors.label
              )}
              {renderSingleWithLabel(
                item,
                "project_count",
                description.items.properties.project_count.label
              )}
            </div>
            {occurrences && (
              <div>
                <span className="varsome-subtitle">
                  {description.items.properties.occurrence.title}
                </span>
                <table className="varsome-table">
                  <thead>
                    <tr>
                      <th>
                        {
                          description.items.properties.occurrence.items
                            .properties.affected.label
                        }
                      </th>
                      <th>
                        {
                          description.items.properties.occurrence.items
                            .properties.donors.label
                        }
                      </th>
                      <th>Frequency</th>
                      <th>
                        {
                          description.items.properties.occurrence.items
                            .properties.project.label
                        }
                      </th>
                      <th>Tumor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {occurrences.map((occurence) => {
                      return (
                        <tr key={uuidv4()}>
                          <td>{occurence.affected}</td>
                          <td>{occurence.donors}</td>
                          <td>{getAlleleFrequency(occurence.frequency)}</td>
                          <td>
                            <div>{occurence.project}</div>
                          </td>
                          <td>
                            {PROJECT_TUMOR.hasOwnProperty(occurence.project)
                              ? PROJECT_TUMOR[occurence.project]
                              : "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      });

    const newIcgc = (
      <React.Fragment>
        {values.map((item) => {
          const {
            oncotree_code: oncotreeCode,
            cancer_type: cancerType,
            cancer_name: cancerName,
            tissue_type: tissueType,
            project_code: projectCode,
          } = item;
          return (
            <div className="tw-p-5" key={uuidv4()}>
              <h5> Summary of results </h5>
              <div className="tw-overflow-y-auto tw-ml-0 tw-mb-8">
                <Table
                  headers={[
                    { id: uuidv4(), title: "Project count" },
                    { id: uuidv4(), title: "Total Donors" },
                    { id: uuidv4(), title: "Affected Donors" },
                    { id: uuidv4(), title: "Tested Donors" },
                    { id: uuidv4(), title: "Frequency" },
                    { id: uuidv4(), title: "Mutation ID" },
                  ]}
                  rows={[
                    {
                      id: uuidv4(),
                      columns: [
                        {
                          id: uuidv4(),
                          content: item.project_count
                            ? item.project_count
                                .toLocaleString()
                                .replace(",", " ")
                            : "-",
                        },
                        {
                          id: uuidv4(),
                          content: item.total_donors
                            ? item.total_donors
                                .toLocaleString()
                                .replace(",", " ")
                            : "-",
                        },
                        {
                          id: uuidv4(),
                          content: item.affected_donors
                            ? item.affected_donors
                                .toLocaleString()
                                .replace(",", " ")
                            : "-",
                        },
                        {
                          id: uuidv4(),
                          content: item.tested_donors
                            ? item.tested_donors
                                .toLocaleString()
                                .replace(",", " ")
                            : "-",
                        },
                        {
                          id: uuidv4(),
                          content: (
                            <React.Fragment>
                              {item.affected_donors && item.tested_donors
                                ? (
                                    item.affected_donors / item.tested_donors
                                  ).toFixed(4)
                                : "-"}
                            </React.Fragment>
                          ),
                        },
                        {
                          id: uuidv4(),
                          content: <div>{item.mutation_id}</div>,
                        },
                      ],
                    },
                  ]}
                  styles={{ overflow: "auto" }}
                />
              </div>
              <div className="varsome-inner-container">
                <ChartsGroup>
                  <Chart
                    showEmptyState={false}
                    data={oncotreeCode}
                    title="Oncotree code"
                  />
                  <Chart
                    showEmptyState={false}
                    data={cancerType}
                    title="Cancer type"
                  />
                  <Chart
                    showEmptyState={false}
                    data={cancerName}
                    title="Cancer name"
                  />
                  <Chart
                    showEmptyState={false}
                    data={tissueType}
                    title="Tissue type"
                  />
                  <Chart
                    data={projectCode}
                    title="Project code chart"
                    showEmptyState={false}
                  />
                </ChartsGroup>
              </div>
            </div>
          );
        })}
      </React.Fragment>
    );

    return (
      <div className="varsome-result-fixed">
        <div className="varsome-container varsome-container__basic">
          <ComponentPanel
            version={version}
            helperPopup={<HelperText fieldDescriptionKey="ICGC Somatic" />}
            titleDetails={renderMainDataPoint(titleDetails)}
            content={
              <React.Fragment>{isOldIcgc ? oldIcgc : newIcgc}</React.Fragment>
            }
            title="ICGC Somatic"
          />
        </div>
      </div>
    );
  }
}

IcgcSomatic.propTypes = {
  icgcSomatic: PropTypes.arrayOf(PropTypes.shape({})),
  icgcSomaticProperties: PropTypes.shape({}),
  isOldIcgc: PropTypes.bool,
};

IcgcSomatic.defaultProps = {
  icgcSomatic: {},
  icgcSomaticProperties: {},
  isOldIcgc: false,
};

const mapStateToProps = (state) => {
  return {
    icgcSomatic: getIcgcSomatic(state),
    icgcSomaticProperties: getIcgcSomaticProperties(state),
    isOldIcgc: getifOldIcgc(state),
  };
};

export default connect(
  mapStateToProps,
  {}
)(withErrorGuard(IcgcSomatic, "IcgcSomatic", "ICGC Somatic"));
